import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../../components/ui/Switch';
import { Card, CardContent } from '../../../components/ui/Card';
import { getChiefSelfService, updateChiefSelfService } from '../../../api/Navigation/moduleConfig';

/**
 * CO-815 — HR's switch for "Chiefs also clock in".
 *
 * Owner decision 2026-09-28 (final): clock-in, timesheet and schedule are OFF for the Chief role by
 * default (web and mobile), and HR can turn them back on for their company where the Chief also clocks.
 * Own payslips are NOT behind this switch: a Chief always keeps them, on web and mobile.
 * This is its own framed section because it saves on its own endpoint the moment it is flipped; it is not
 * part of the Attendance Rules form above and must not wait for that form's Save button.
 *
 * HR only (server policy HrOnly). The caller renders it for HR and nobody else.
 */
const ChiefSelfServiceCard: React.FC = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getChiefSelfService();
        if (cancelled) return;
        if (result?.errors?.length) {
          setError(t('chiefRole.selfService.loadFailed'));
        } else {
          setEnabled(!!result?.payload?.enabled);
        }
      } catch {
        if (!cancelled) setError(t('chiefRole.selfService.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const handleChange = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const result = await updateChiefSelfService(next);
      if (result?.errors?.length) {
        setEnabled(previous);
        setError(t('chiefRole.selfService.saveFailed'));
      } else {
        setSaved(true);
      }
    } catch {
      setEnabled(previous);
      setError(t('chiefRole.selfService.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="chief-self-service-card">
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-foreground">{t('chiefRole.selfService.title')}</h3>
          <p className="text-sm text-muted-foreground max-w-3xl">{t('chiefRole.selfService.body')}</p>
        </div>

        <Switch
          id="chief-self-service-switch"
          checked={enabled}
          onCheckedChange={handleChange}
          disabled={loading || saving}
          label={t('chiefRole.selfService.label')}
          description={t('chiefRole.selfService.description')}
        />

        <p className="text-xs min-h-4" aria-live="polite" data-testid="chief-self-service-status">
          {error ? (
            <span className="text-error">{error}</span>
          ) : saved ? (
            <span className="text-muted-foreground">{t('chiefRole.selfService.saved')}</span>
          ) : null}
        </p>
      </CardContent>
    </Card>
  );
};

export default ChiefSelfServiceCard;
